import { useRef, useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'

/**
 * MediaPlayer - Synchronized Media Playback Component
 *
 * Supports:
 * - YouTube videos (iframe embed)
 * - HTML5 video (mp4, webm, etc.)
 * - HTML5 audio (mp3, wav, etc.)
 * - Images (with auto-advance timer)
 *
 * Real-time sync:
 * - Syncs playback state with session updates
 * - Detects drift and corrects playback position
 * - Follows leader's play/pause/seek commands
 *
 * @param {Object} session - Media session state from useMediaSession (new schema)
 * @param {Function} onTimeUpdate - Callback for time updates (for scrubber)
 * @param {Function} onEnded - Callback when media ends (play next)
 * @param {Boolean} canControl - Whether user can control playback
 */
export default function MediaPlayer({
  session,
  onTimeUpdate,
  onEnded,
  canControl = false
}) {
  const videoRef = useRef(null)
  const audioRef = useRef(null)
  const youtubePlayerRef = useRef(null)
  const [isLoading, setIsLoading] = useState(true)
  const [imageTimer, setImageTimer] = useState(null)
  const lastSyncTime = useRef(0)
  const loadingTimeoutRef = useRef(null)

  // YouTube Player API
  const [youtubeReady, setYoutubeReady] = useState(false)

  // Reset loading state when session changes and add timeout fallback
  useEffect(() => {
    if (session) {
      setIsLoading(true)

      // Clear any existing timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }

      // Fallback: auto-clear loading after 10 seconds (in case something fails)
      loadingTimeoutRef.current = setTimeout(() => {
        setIsLoading(false)
      }, 10000)
    }

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
    }
  }, [session?.id, session?.video_url])

  // Helper: Check if session is playing
  const isPlaying = session?.state === 'playing'

  // Load YouTube IFrame API
  useEffect(() => {
    if (session?.platform === 'youtube' && !window.YT) {
      const tag = document.createElement('script')
      tag.src = 'https://www.youtube.com/iframe_api'
      const firstScriptTag = document.getElementsByTagName('script')[0]
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)

      window.onYouTubeIframeAPIReady = () => {
        setYoutubeReady(true)
      }
    } else if (window.YT) {
      setYoutubeReady(true)
    }
  }, [session?.platform])

  // Initialize YouTube Player
  useEffect(() => {
    if (session?.platform === 'youtube' && youtubeReady && !youtubePlayerRef.current) {
      const videoId = session.video_id || extractYouTubeId(session.video_url)
      if (videoId) {
        youtubePlayerRef.current = new window.YT.Player('youtube-player', {
          videoId: videoId,
          playerVars: {
            autoplay: 0,
            controls: 0, // Hide controls (we provide our own)
            disablekb: !canControl ? 1 : 0,
            modestbranding: 1,
            rel: 0,
            playsinline: 1
          },
          events: {
            onReady: () => {
              // Clear the timeout since loading succeeded
              if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current)
              }
              setIsLoading(false)
              syncYouTubePlayer()
            },
            onStateChange: (event) => {
              // State: UNSTARTED (-1), ENDED (0), PLAYING (1), PAUSED (2), BUFFERING (3), CUED (5)
              if (event.data === 0) {
                // Video ended
                onEnded?.()
              }
            }
          }
        })
      }
    }

    return () => {
      if (youtubePlayerRef.current?.destroy) {
        youtubePlayerRef.current.destroy()
        youtubePlayerRef.current = null
      }
    }
  }, [session?.platform, session?.video_url, youtubeReady])

  // Sync YouTube player with session state
  const syncYouTubePlayer = () => {
    if (!youtubePlayerRef.current || !session) return

    try {
      const player = youtubePlayerRef.current

      // Sync play/pause
      const playerState = player.getPlayerState()
      if (isPlaying && playerState !== 1) {
        player.playVideo()
      } else if (!isPlaying && playerState === 1) {
        player.pauseVideo()
      }

      // Sync time (check for drift)
      const currentTime = player.getCurrentTime()
      const drift = Math.abs(currentTime - (session.playback_position || 0))
      if (drift > 2) {
        // Drift > 2 seconds, seek to correct position
        player.seekTo(session.playback_position || 0, true)
      }
    } catch (error) {
      console.error('Error syncing YouTube player:', error)
    }
  }

  // Extract YouTube video ID from URL
  const extractYouTubeId = (url) => {
    if (!url) return null
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/
    ]

    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match) return match[1]
    }
    return null
  }

  // Sync video/audio player with session state
  useEffect(() => {
    if (!session) return

    const mediaElement = session.platform === 'audio' ? audioRef.current : videoRef.current

    if (mediaElement) {
      // Sync play/pause
      if (isPlaying && mediaElement.paused) {
        mediaElement.play().catch(err => {
          console.error('Error playing media:', err)
        })
      } else if (!isPlaying && !mediaElement.paused) {
        mediaElement.pause()
      }

      // Sync time (check for drift)
      const drift = Math.abs(mediaElement.currentTime - (session.playback_position || 0))
      if (drift > 1) {
        // Drift > 1 second, seek to correct position
        mediaElement.currentTime = session.playback_position || 0
      }
    }

    // Sync YouTube player
    if (session.platform === 'youtube') {
      syncYouTubePlayer()
    }
  }, [session?.state, session?.playback_position])

  // Handle video/audio time updates
  const handleTimeUpdate = (e) => {
    const currentTime = e.target.currentTime
    const duration = e.target.duration

    // Throttle updates to avoid spamming
    const now = Date.now()
    if (now - lastSyncTime.current > 1000) {
      onTimeUpdate?.({
        currentTime,
        duration
      })
      lastSyncTime.current = now
    }
  }

  // Handle media ended
  const handleEnded = () => {
    onEnded?.()
  }

  // Handle media loaded
  const handleLoadedMetadata = () => {
    // Clear the timeout since loading succeeded
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current)
    }
    setIsLoading(false)
  }

  // Image auto-advance timer
  useEffect(() => {
    if (session?.platform === 'image' && isPlaying) {
      // Auto-advance after 10 seconds
      const timer = setTimeout(() => {
        onEnded?.()
      }, 10000)

      setImageTimer(timer)

      return () => clearTimeout(timer)
    }
  }, [session?.platform, session?.state, session?.video_url])

  // Render different media types
  const renderMedia = () => {
    if (!session) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-gjyl-slate-900">
          <div className="text-center space-y-4">
            <div className="text-6xl opacity-30">🎬</div>
            <p className="text-slate-500">No media playing</p>
          </div>
        </div>
      )
    }

    switch (session.platform) {
      case 'youtube':
        return (
          <div className="relative w-full h-full bg-black">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gjyl-slate-900">
                <Loader2 className="w-12 h-12 text-gjyl-indigo-500 animate-spin" />
              </div>
            )}
            <div
              id="youtube-player"
              className="w-full h-full"
            />
          </div>
        )

      case 'video':
        return (
          <div className="relative w-full h-full bg-black">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gjyl-slate-900">
                <Loader2 className="w-12 h-12 text-gjyl-indigo-500 animate-spin" />
              </div>
            )}
            <video
              ref={videoRef}
              src={session.video_url}
              className="w-full h-full object-contain"
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleEnded}
              onLoadedMetadata={handleLoadedMetadata}
              playsInline
            />
          </div>
        )

      case 'audio':
        return (
          <div className="relative w-full h-full bg-gradient-to-br from-gjyl-slate-900 via-gjyl-slate-800 to-gjyl-slate-900 flex items-center justify-center">
            {/* Audio waveform visualization placeholder */}
            <div className="text-center space-y-6">
              <div className="text-8xl animate-pulse">🎵</div>
              <div className="space-y-2">
                <h3 className="text-2xl font-semibold text-white">
                  {session.title || 'Audio Playback'}
                </h3>
                {session.thumbnail_url && (
                  <img
                    src={session.thumbnail_url}
                    alt="Album art"
                    className="w-64 h-64 mx-auto rounded-lg shadow-gjyl-lift object-cover"
                  />
                )}
              </div>
            </div>
            <audio
              ref={audioRef}
              src={session.video_url}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleEnded}
              onLoadedMetadata={handleLoadedMetadata}
            />
          </div>
        )

      case 'image':
        return (
          <div className="relative w-full h-full bg-gjyl-slate-900 flex items-center justify-center">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-12 h-12 text-gjyl-indigo-500 animate-spin" />
              </div>
            )}
            <img
              src={session.video_url}
              alt={session.title || 'Image'}
              className="max-w-full max-h-full object-contain"
              onLoad={() => {
                if (loadingTimeoutRef.current) {
                  clearTimeout(loadingTimeoutRef.current)
                }
                setIsLoading(false)
              }}
            />
            {isPlaying && (
              <div className="absolute bottom-4 right-4 glass rounded-full px-4 py-2 text-sm text-white">
                Auto-advancing in {Math.ceil((imageTimer || 10000) / 1000)}s
              </div>
            )}
          </div>
        )

      case 'link':
      default:
        return (
          <div className="w-full h-full flex items-center justify-center bg-gjyl-slate-900">
            <div className="text-center space-y-4 p-8">
              <div className="text-6xl">🔗</div>
              <h3 className="text-xl font-semibold text-white">External Link</h3>
              <p className="text-slate-400 max-w-md">{session.video_url}</p>
              <a
                href={session.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-block"
              >
                Open Link
              </a>
            </div>
          </div>
        )
    }
  }

  return (
    <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
      {renderMedia()}

      {/* Media info overlay (bottom-left) */}
      {session && (
        <div className="absolute bottom-4 left-4 glass rounded-lg px-4 py-2 max-w-md">
          <div className="flex items-center gap-3">
            {session.thumbnail_url && session.platform !== 'image' && (
              <img
                src={session.thumbnail_url}
                alt=""
                className="w-12 h-12 rounded object-cover"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {session.title || session.video_url}
              </p>
              <p className="text-xs text-slate-400 capitalize">
                {session.platform}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Leader info (top-right) */}
      {session?.leader && !canControl && (
        <div className="absolute top-4 right-4 glass rounded-full px-4 py-2 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-gjyl-indigo-500 rounded-full animate-pulse" />
            <span className="text-slate-300">
              Controlled by{' '}
              <span className="text-white font-medium">
                @{session.leader.username}
              </span>
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
